import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  Users,
  Search,
  Download,
  Trash2,
  Mail,
  Globe,
  Calendar,
  FileDown,
  Check,
  Copy,
} from 'lucide-react'
import { api, type Lead } from '@/lib/api'
import { formatDate, copyToClipboard } from '@/lib/utils'
import toast from 'react-hot-toast'

export function LeadsPage() {
  const [search, setSearch] = useState('')
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null)
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set())
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [assigningLeadId, setAssigningLeadId] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const { data: sitesData } = useQuery({
    queryKey: ['sites'],
    queryFn: () => api.getSites(),
  })

  const { data, isLoading } = useQuery({
    queryKey: ['leads', selectedSiteId],
    queryFn: () => api.getLeads(selectedSiteId || undefined),
  })

  const { data: teamData } = useQuery({
    queryKey: ['team-members'],
    queryFn: () => api.getTeamMembers(),
  })

  const members = teamData?.members || []

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteLead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      toast.success('Lead deleted')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const assignMutation = useMutation({
    mutationFn: ({ leadId, memberId }: { leadId: string; memberId: string | null }) =>
      api.updateLead(leadId, { assignedTo: memberId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      toast.success('Lead assigned')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
    onSettled: () => {
      setAssigningLeadId(null)
    },
  })

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const blob = await api.exportLeads(selectedSiteId || undefined, format)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `leads-${new Date().toISOString().split('T')[0]}.${format}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast.success(`Exported as ${format.toUpperCase()}`)
    } catch (error: any) {
      toast.error(error.message)
    }
  }

  const handleCopyEmail = async (email: string, leadId: string) => {
    await copyToClipboard(email)
    setCopiedId(leadId)
    setTimeout(() => setCopiedId(null), 2000)
    toast.success('Email copied!')
  }

  const handleSelectAll = () => {
    if (selectedLeads.size === filteredLeads?.length) {
      setSelectedLeads(new Set())
    } else {
      setSelectedLeads(new Set(filteredLeads?.map((lead: Lead) => lead.id)))
    }
  }

  const handleSelectLead = (id: string) => {
    const newSelected = new Set(selectedLeads)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedLeads(newSelected)
  }

  const handleAssignLead = (leadId: string, memberId: string | null) => {
    setAssigningLeadId(leadId)
    assignMutation.mutate({ leadId, memberId })
  }

  const handleBulkDelete = async () => {
    if (!confirm(`Delete ${selectedLeads.size} leads?`)) return

    for (const id of selectedLeads) {
      await deleteMutation.mutateAsync(id)
    }
    setSelectedLeads(new Set())
  }

  const filteredLeads = data?.leads?.filter((lead: Lead) =>
    lead.email.toLowerCase().includes(search.toLowerCase()) ||
    lead.name?.toLowerCase().includes(search.toLowerCase())
  )

  const totalLeads = data?.leads?.length || 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold">Leads</h1>
          <p className="text-muted-foreground">
            {totalLeads} total leads captured
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <button className="btn-outline">
              <FileDown className="h-4 w-4" />
              Export
            </button>
            <div className="absolute right-0 mt-2 w-40 rounded-lg border border-border bg-card shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
              <button
                onClick={() => handleExport('csv')}
                className="w-full px-4 py-2 text-left text-sm hover:bg-muted rounded-t-lg"
              >
                Export as CSV
              </button>
              <button
                onClick={() => handleExport('json')}
                className="w-full px-4 py-2 text-left text-sm hover:bg-muted rounded-b-lg"
              >
                Export as JSON
              </button>
            </div>
          </div>
          <button onClick={() => handleExport('csv')} className="btn-primary">
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by email or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10"
          />
        </div>

        <select
          value={selectedSiteId || ''}
          onChange={(e) => setSelectedSiteId(e.target.value || null)}
          className="input sm:w-64"
        >
          <option value="">All Sites</option>
          {sitesData?.sites?.map((site: any) => (
            <option key={site.id} value={site.id}>
              {site.domain_name}
            </option>
          ))}
        </select>
      </div>

      {/* Bulk Actions */}
      {selectedLeads.size > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 p-4 rounded-lg bg-primary/10 border border-primary/20"
        >
          <span className="text-sm font-medium">
            {selectedLeads.size} lead{selectedLeads.size > 1 ? 's' : ''} selected
          </span>
          <div className="flex-1" />
          <button
            onClick={handleBulkDelete}
            className="btn-ghost btn-sm text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4" />
            Delete Selected
          </button>
        </motion.div>
      )}

      {/* Leads Table */}
      {isLoading ? (
        <div className="card">
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        </div>
      ) : filteredLeads?.length === 0 ? (
        <div className="card text-center py-12">
          <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">
            {search ? 'No leads found' : 'No leads yet'}
          </h3>
          <p className="text-muted-foreground">
            {search
              ? 'Try a different search term'
              : 'Leads will appear here when visitors submit forms on your sites'}
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="p-4 text-left">
                    <input
                      type="checkbox"
                      checked={selectedLeads.size === filteredLeads?.length}
                      onChange={handleSelectAll}
                      className="rounded border-border"
                    />
                  </th>
                  <th className="p-4 text-left text-sm font-semibold">Email</th>
                  <th className="p-4 text-left text-sm font-semibold">Name</th>
                  <th className="p-4 text-left text-sm font-semibold">Site</th>
                  <th className="p-4 text-left text-sm font-semibold">Assignee</th>
                  <th className="p-4 text-left text-sm font-semibold">Date</th>
                  <th className="p-4 text-right text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads?.map((lead: Lead, i: number) => (
                  <motion.tr
                    key={lead.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: i * 0.02 }}
                    className="border-b border-border last:border-0 hover:bg-muted/50"
                  >
                    <td className="p-4">
                      <input
                        type="checkbox"
                        checked={selectedLeads.has(lead.id)}
                        onChange={() => handleSelectLead(lead.id)}
                        className="rounded border-border"
                      />
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                          <Mail className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-medium">{lead.email}</p>
                          {lead.customFields && Object.keys(lead.customFields).length > 0 && (
                            <p className="text-xs text-muted-foreground">
                              +{Object.keys(lead.customFields).length} custom fields
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={lead.name ? '' : 'text-muted-foreground'}>
                        {lead.name || '—'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm truncate max-w-[150px]">
                          {lead.domainName || lead.siteId}
                        </span>
                      </div>
                    </td>
                    <td className="p-4">
                      <select
                        value={lead.assignedTo?.id || ''}
                        onChange={(e) =>
                          handleAssignLead(lead.id, e.target.value || null)
                        }
                        disabled={assigningLeadId === lead.id}
                        className="input h-9 text-sm"
                      >
                        <option value="">Unassigned</option>
                        {members.map((member) => (
                          <option key={member.id} value={member.id}>
                            {member.name || member.email}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span className="text-sm">{formatDate(lead.createdAt)}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleCopyEmail(lead.email, lead.id)}
                          className="btn-ghost p-2"
                          title="Copy email"
                        >
                          {copiedId === lead.id ? (
                            <Check className="h-4 w-4 text-success" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => {
                            if (confirm('Delete this lead?')) {
                              deleteMutation.mutate(lead.id)
                            }
                          }}
                          className="btn-ghost p-2 text-destructive hover:bg-destructive/10"
                          title="Delete lead"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-primary/10 text-primary">
              <Users className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold">{totalLeads}</p>
              <p className="text-sm text-muted-foreground">Total Leads</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-success/10 text-success">
              <Calendar className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold">
                {data?.leads?.filter((l: Lead) => {
                  const leadDate = new Date(l.createdAt)
                  const today = new Date()
                  return leadDate.toDateString() === today.toDateString()
                }).length || 0}
              </p>
              <p className="text-sm text-muted-foreground">Today</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-secondary/10 text-secondary">
              <Globe className="h-6 w-6" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold">
                {new Set(data?.leads?.map((l: Lead) => l.siteId)).size || 0}
              </p>
              <p className="text-sm text-muted-foreground">Active Sites</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
